import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import BarraFiltri from "@/components/BarraFiltri";
import CampoFirma from "@/components/CampoFirma";
import CercaMezzo from "@/components/CercaMezzo";
import { toast } from "sonner";
import {
  Plus, Printer, Trash2, Truck, Pen, PenLine, X, FileText, UploadCloud,
  Ban,
} from "lucide-react";

/**
 * I documenti di trasporto.
 *
 * Prima erano due cose separate che si dovevano tenere allineate a mano: il
 * modulo da compilare e stampare, e un foglio Excel dove si ribatteva numero,
 * data, autore, destinazione e descrizione. Qui è una cosa sola — l'elenco
 * è il registro — e il numero lo assegna il progressivo dell'anno, che è la
 * cosa che a mano si sbagliava.
 *
 * Le firme si prendono col dito sul posto: chi consegna, chi riceve, il
 * vettore e il conducente. Quelle prese si stampano già dentro il modulo;
 * quelle che mancano lasciano il riquadro vuoto, perché è lì che si firma a
 * penna quando il telefono non c'è.
 */
const giorno = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");
const oggi = () => new Date().toISOString().substring(0, 10);

const VUOTO = {
  data: oggi(), autore: "", cliente: "", cliente_esteso: "", destinazione: "",
  causale: "", porto: "", descrizione: "", aspetto: "A VISTA", colli: "",
  peso: "", data_ritiro: "", ora_ritiro: "", vettore: "",
  trasporto_a_cura: "Mittente", note: "",
  righe: [{ articolo: "", descrizione: "", um: "N°", quantita: "" }],
};

/**
 * Un campo del modulo.
 *
 * Sta fuori dal componente che lo usa, e deve restarci: definito dentro,
 * React lo vede come un componente nuovo a ogni battuta di tasto, smonta la
 * casella e la rimonta, e il cursore salta fuori dopo ogni lettera.
 */
function Campo({ etichetta, chiave, valore, onChange, tipo = "text", lista, larga }) {
  return (
    <div className={larga ? "sm:col-span-2" : ""}>
      <Label>{etichetta}</Label>
      <Input type={tipo} value={valore ?? ""} data-testid={`ddt-${chiave}`}
             list={lista ? `l-${chiave}` : undefined}
             onChange={(e) => onChange(e.target.value)} />
      {lista && (
        <datalist id={`l-${chiave}`}>
          {lista.map((o) => <option key={o} value={o} />)}
        </datalist>
      )}
    </div>
  );
}

function ModuloDDT({ rif, ddt, onChiudi, onSalvato }) {
  const nuovo = !ddt;
  // Un documento salvato senza merce torna indietro con zero righe, ed e'
  // giusto che sia cosi' nel database. A video pero' serve sempre una riga
  // in cui scrivere: senza, il riquadro resta vuoto e la merce su quel
  // documento non si puo' piu' aggiungere, perche' le righe nuove nascono
  // solo scrivendo nell'ultima.
  const [f, setF] = useState(() => {
    const base = ddt ? { ...VUOTO, ...ddt } : { ...VUOTO };
    if (!base.righe?.length) base.righe = [...VUOTO.righe];
    return base;
  });
  const [salvo, setSalvo] = useState(false);
  const [firme, setFirme] = useState(() => (ddt?.firme || {}));
  const su = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  // Scegliendo un mezzo dal parco la riga si compila da sola e resta legata
  // a quel mezzo: da lì nasce il cambio di stato sulla sua scheda e il
  // collegamento per risalire a questo documento.
  const scegliMezzo = (i, id) =>
    setF((x) => {
      const m = (rif?.mezzi || []).find((y) => y.id === id);
      const righe = x.righe.map((r, k) => (k !== i ? r : (m ? {
        ...r, equipment_id: m.id, articolo: m.identifier,
        descrizione: [m.brand, m.model].filter(Boolean).join(" ") || m.category,
        um: "N°", quantita: r.quantita || 1,
      } : { ...r, equipment_id: null })));
      const ultima = righe[righe.length - 1];
      if (!ultima || ultima.articolo || ultima.descrizione || ultima.quantita) {
        righe.push({ articolo: "", descrizione: "", um: "N°", quantita: "" });
      }
      return { ...x, righe };
    });

  const cambiaRiga = (i, campo, valore) =>
    setF((x) => {
      const righe = x.righe.map((r, k) => (k === i ? { ...r, [campo]: valore } : r));
      // Se si scrive sull'ultima riga se ne apre un'altra: si compila di
      // seguito senza dover toccare "aggiungi" ogni volta.
      const ultima = righe[righe.length - 1];
      if (!ultima || ultima.articolo || ultima.descrizione || ultima.quantita) {
        righe.push({ articolo: "", descrizione: "", um: "N°", quantita: "" });
      }
      return { ...x, righe };
    });

  const aggiungiRiga = () =>
    setF((x) => ({ ...x,
      righe: [...(x.righe || []),
              { articolo: "", descrizione: "", um: "N°", quantita: "" }] }));

  const togliRiga = (i) =>
    setF((x) => {
      const righe = x.righe.filter((_, k) => k !== i);
      return { ...x, righe: righe.length ? righe : [...VUOTO.righe] };
    });

  const salva = async () => {
    if (!f.cliente?.trim()) { toast.error("Manca il cliente"); return; }
    setSalvo(true);
    try {
      const corpo = {
        ...f,
        colli: f.colli === "" ? null : Number(f.colli),
        peso: f.peso === "" ? null : Number(f.peso),
        righe: (f.righe || [])
          .filter((r) => r.articolo || r.descrizione || r.quantita)
          .map((r) => ({ ...r, quantita: r.quantita === "" ? null : Number(r.quantita) })),
      };
      const { data } = nuovo
        ? await api.post("/ddt", corpo)
        : await api.put(`/ddt/${ddt.id}`, corpo);
      toast.success(nuovo ? `DDT ${data.numero} creato` : "DDT aggiornato");
      onSalvato(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSalvo(false); }
  };

  const firma = async (chi, valore) => {
    if (nuovo) { toast.error("Salva il DDT prima di firmarlo"); return; }
    try {
      await api.put(`/ddt/${ddt.id}/firma`, { chi, firma: valore });
      setFirme((x) => {
        const n = { ...x };
        if (valore) n[chi] = valore; else delete n[chi];
        return n;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella firma");
    }
  };

  // Una scorciatoia per non ripetere valore e onChange su venti campi. Non è
  // un componente: è solo l'oggetto delle proprietà, così `Campo` resta lo
  // stesso fra un render e l'altro e il cursore non salta.
  const c = (chiave) => ({ chiave, valore: f[chiave], onChange: su(chiave) });

  // Che cosa farà questa causale ai mezzi indicati nelle righe. Si dice
  // prima di salvare: cambiare la disponibilità di un mezzo vero senza
  // averlo scritto da nessuna parte sarebbe una sorpresa.
  const effetto = (rif?.effetti || {})[(f.causale || "").trim().toUpperCase()];

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {nuovo ? "Nuovo documento di trasporto"
                   : `Documento di trasporto ${ddt.numero}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {ddt?.annullato && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <strong>Documento annullato</strong> da {ddt.annullato.da} il{" "}
              {String(ddt.annullato.il || "").substring(0, 10).split("-").reverse().join("/")}.
              <br />Motivo: {ddt.annullato.motivo}
              <br />
              <span className="text-xs">
                Resta qui da leggere per intero. Non si corregge e non si firma.
              </span>
            </div>
          )}
          {nuovo && (
            <p className="text-xs text-slate-500">
              Il numero lo assegna l&apos;app quando salvi: il prossimo è il{" "}
              <strong>{rif?.prossimo_numero}</strong>.
            </p>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <Campo etichetta="Data" {...c("data")} tipo="date" />
            <Campo etichetta="Autore" {...c("autore")} lista={rif?.autori || []} />
            <Campo etichetta="Cliente" {...c("cliente")}
                   lista={(rif?.clienti || []).map((x) => x.name)} />
            <Campo etichetta="Destinazione merce, se diversa" {...c("destinazione")} />
          </div>

          <div>
            <Label>Cliente per esteso, come va stampato</Label>
            <textarea value={f.cliente_esteso || ""} rows={3}
                      data-testid="ddt-cliente_esteso"
                      onChange={(e) => su("cliente_esteso")(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <p className="text-[11px] text-slate-400 mt-0.5">
              Ragione sociale e indirizzo, una riga per volta. Se lo lasci vuoto
              va il nome del cliente.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Campo etichetta="Causale del trasporto" {...c("causale")} lista={rif?.causali || []} />
            <Campo etichetta="Descrizione, per il registro" {...c("descrizione")} />
            <Campo etichetta="Porto" {...c("porto")} lista={rif?.porto || []} />
            <Campo etichetta="Aspetto esteriore dei beni" {...c("aspetto")} lista={rif?.aspetto || []} />
          </div>

          <div className="border border-slate-200 rounded-lg p-3">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" /> Merce trasportata
            </div>
            <div className="space-y-2">
              {(f.righe || []).map((r, i) => (
                <div key={i} className="space-y-1">
                <div className="grid grid-cols-12 gap-1.5 items-center">
                  <CercaMezzo mezzi={rif?.mezzi} indice={i}
                              classe="col-span-12"
                              segnaposto="Merce generica — oppure cerca un mezzo o un'attrezzatura dell'azienda"
                              valore={r.equipment_id}
                              onScegli={(id) => scegliMezzo(i, id)} />
                  <Input className="col-span-3" placeholder="Articolo"
                         value={r.articolo || ""} data-testid={`riga-articolo-${i}`}
                         onChange={(e) => cambiaRiga(i, "articolo", e.target.value)} />
                  <Input className="col-span-5" placeholder="Descrizione"
                         value={r.descrizione || ""} data-testid={`riga-descrizione-${i}`}
                         onChange={(e) => cambiaRiga(i, "descrizione", e.target.value)} />
                  <select className="col-span-2 h-10 border border-slate-200 rounded-lg px-2 text-sm bg-white"
                          value={r.um || "N°"}
                          onChange={(e) => cambiaRiga(i, "um", e.target.value)}>
                    {(rif?.unita || ["N°"]).map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <Input className="col-span-1" placeholder="Q.tà" inputMode="decimal"
                         value={r.quantita ?? ""} data-testid={`riga-quantita-${i}`}
                         onChange={(e) => cambiaRiga(i, "quantita", e.target.value)} />
                  <button type="button" onClick={() => togliRiga(i)}
                          className="col-span-1 text-slate-400 hover:text-red-600 flex justify-center">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {r.equipment_id && effetto && (
                  <p className="text-[11px] text-amber-700 pl-1">
                    Salvando, questo mezzo risulterà <strong>{effetto.nota}</strong> sulla
                    sua scheda, e non più disponibile finché non rientra.
                  </p>
                )}
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={aggiungiRiga}
                    className="mt-2" data-testid="ddt-aggiungi-riga">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi riga
            </Button>
          </div>

          <div className="grid sm:grid-cols-4 gap-3">
            <Campo etichetta="Colli" {...c("colli")} tipo="number" />
            <Campo etichetta="Peso kg" {...c("peso")} tipo="number" />
            <Campo etichetta="Data del ritiro" {...c("data_ritiro")} tipo="date" />
            <Campo etichetta="Ora" {...c("ora_ritiro")} tipo="time" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Trasporto a cura di</Label>
              <select value={f.trasporto_a_cura || "Mittente"}
                      data-testid="ddt-trasporto_a_cura"
                      onChange={(e) => su("trasporto_a_cura")(e.target.value)}
                      className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                {(rif?.trasporto_a_cura || ["Mittente"]).map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </div>
            <Campo etichetta="Vettore" {...c("vettore")} />
          </div>

          <div>
            <Label>Note</Label>
            <textarea value={f.note || ""} rows={2} data-testid="ddt-note"
                      onChange={(e) => su("note")(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Le firme si prendono solo su un DDT già salvato: prima non
              esiste ancora il documento a cui attaccarle. */}
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2">
              <PenLine className="w-4 h-4 text-orange-500" /> Firme
            </div>
            {nuovo ? (
              <p className="text-sm text-slate-500">
                Salva il documento e poi riaprilo per firmarlo a video.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {(rif?.firme || []).map((x) => (
                  <CampoFirma key={x.codice} etichetta={x.nome}
                              valore={firme[x.codice] || null}
                              onChange={(v) => firma(x.codice, v)} />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {!ddt?.annullato && (
            <Button onClick={salva} disabled={salvo} data-testid="salva-ddt"
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {salvo ? "Salvo..." : nuovo ? "Crea il DDT" : "Salva le modifiche"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Il caricamento dello storico dal registro Excel.
 *
 * Prima si guarda cosa succederebbe, poi si conferma: su un documento fiscale
 * caricare due volte lo stesso registro vorrebbe dire avere due DDT con lo
 * stesso numero, e non si torna indietro. Il numero è il nome proprio del
 * documento, quindi è lui a dire cosa c'è già.
 */
function ImportaStorico({ onFatto, onChiudi }) {
  const [file, setFile] = useState(null);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [esito, setEsito] = useState(null);
  const [lavoro, setLavoro] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) { toast.error("Scegli il file"); return; }
    setLavoro(true);
    try {
      const modulo = new FormData();
      modulo.append("file", file);
      modulo.append("solo_verifica", soloVerifica ? "true" : "false");
      if (anno) modulo.append("anno", anno);
      const { data } = await api.post("/ddt/importa", modulo);
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.importate} DDT caricati`);
        onFatto();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setLavoro(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Carica lo storico dei DDT</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <strong className="text-amber-800">Nessun numero entra due volte.</strong>{" "}
            Il numero è il nome proprio del DDT: se è già a registro quella riga
            non entra, e se è ripetuto dentro il file entra una volta sola. Dopo
            il caricamento la numerazione riparte dall&apos;ultimo numero visto,
            così il prossimo DDT scritto a mano non ci va a sbattere.
            <br />
            Del registro si prendono numero, data, autore, destinazione,
            descrizione e note: le righe della merce lì dentro non ci sono, e si
            aggiungono aprendo il documento.
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>File Excel</Label>
              <Input type="file" accept=".xlsx,.xlsm,.xls"
                     data-testid="file-storico-ddt"
                     onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
            </div>
            <div>
              <Label>Anno da caricare</Label>
              <Input value={anno} data-testid="anno-storico-ddt"
                     placeholder="tutti gli anni"
                     onChange={(e) => { setAnno(e.target.value); setEsito(null); }} />
              <p className="text-[11px] text-slate-400 mt-0.5">
                Lascialo vuoto per caricarli tutti.
              </p>
            </div>
          </div>

          {esito && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["Nel file", esito.righe_nel_file],
                  ["Dell'anno", esito.righe_dell_anno],
                  ["Da caricare", esito.da_importare],
                  ["Già presenti", esito.gia_presenti]].map(([n, v]) => (
                  <div key={n} className="border border-slate-200 rounded p-2">
                    <div className="font-display text-xl font-black tabular-nums">{v}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{n}</div>
                  </div>
                ))}
              </div>
              {esito.dal && (
                <p className="text-xs text-slate-500">
                  Dal {giorno(esito.dal)} al {giorno(esito.al)}
                  {(esito.anni_nel_file || []).length > 1
                    && ` · nel file ci sono gli anni ${esito.anni_nel_file.join(", ")}`}
                </p>
              )}
              {(esito.gia_a_registro || []).length > 0 && (
                <p className="text-xs text-slate-600">
                  <span className="font-semibold">{esito.gia_a_registro.length}</span>{" "}
                  già a registro:{" "}
                  {esito.gia_a_registro.slice(0, 8).map((x) => x.numero).join(" · ")}
                  {esito.gia_a_registro.length > 8 && " …"}
                </p>
              )}
              {(esito.doppie_nel_file || []).length > 0 && (
                <p className="text-xs text-amber-700">
                  <span className="font-semibold">{esito.doppie_nel_file.length}</span>{" "}
                  numeri ripetuti dentro il file, ne entra uno solo:{" "}
                  {esito.doppie_nel_file.slice(0, 8).map((x) => x.numero).join(" · ")}
                </p>
              )}
              {(esito.scartate || []).length > 0 && (
                <p className="text-xs text-red-700">
                  <span className="font-semibold">{esito.scartate.length}</span>{" "}
                  righe non leggibili:{" "}
                  {esito.scartate.slice(0, 6).map((s) => `riga ${s.riga} (${s.perche})`).join(" · ")}
                </p>
              )}
              {(esito.avvisi || []).map((a, i) => (
                <p key={i} className="text-xs text-slate-500">{a}</p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          <Button variant="outline" onClick={() => manda(true)} disabled={lavoro || !file}
                  data-testid="verifica-storico-ddt">
            Guarda cosa succede
          </Button>
          <Button onClick={() => manda(false)}
                  disabled={lavoro || !esito || !esito.da_importare}
                  data-testid="carica-storico-ddt"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {esito?.da_importare ? `Carica ${esito.da_importare} DDT` : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
  * L'annullamento di un documento di trasporto.
  *
  * Il documento non sparisce: resta a registro con una riga sopra, e chiunque
  * può riaprirlo e leggere cosa c'era scritto dentro. Un DDT che sparisce non
  * è verificabile da nessuno — chi si chiede perché fra il 28 e il 30 manchi
  * il 29 non ha modo di saperlo.
  *
  * Si riscrive il numero, che è la stessa fatica di leggerlo e ferma la mano
  * quando si sta annullando la riga sbagliata, e si scrive la ragione: è
  * l'unica cosa che fra un anno spiega perché quel documento non vale più.
  */
function ConfermaAnnullamento({ ddt, onChiudi, onConferma }) {
  const [scritto, setScritto] = useState("");
  const [motivo, setMotivo] = useState("");
  const giusto = scritto.trim() === String(ddt.numero || "").trim();
  const spiegato = motivo.trim().length >= 5;

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700">
            Annullare il DDT {ddt.numero}?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-200 rounded p-3 space-y-1.5">
            <p>
              <strong>Resta a registro, barrato.</strong> Si potrà sempre
              riaprire e leggere cosa conteneva, con scritto chi l&apos;ha
              annullato, quando e perché.
            </p>
            <p>
              Il numero <strong>{ddt.numero}</strong> resta suo: nessun altro
              documento lo riprenderà.
            </p>
            <p>
              Non si potrà più correggere né firmare, e i mezzi usciti con
              questo documento tornano nello stato che avevano prima.
            </p>
          </div>

          <div className="text-xs text-slate-600">
            {ddt.data && `${ddt.data.substring(0, 10).split("-").reverse().join("/")} · `}
            {ddt.cliente}{ddt.descrizione ? ` · ${ddt.descrizione}` : ""}
          </div>

          <div>
            <Label>Perché lo annulli</Label>
            <textarea value={motivo} rows={2} data-testid="motivo-annullamento"
                      placeholder="Es. compilato per il cliente sbagliato"
                      onChange={(e) => setMotivo(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div>
            <Label>Riscrivi il numero per confermare</Label>
            <Input value={scritto} placeholder={ddt.numero}
                   data-testid="conferma-numero-ddt"
                   onChange={(e) => setScritto(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Lascialo com&apos;è</Button>
          <Button onClick={() => onConferma(motivo.trim())}
                  disabled={!giusto || !spiegato}
                  data-testid="elimina-ddt-conferma"
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
            <Ban className="w-4 h-4 mr-2" />
            {!spiegato ? "Scrivi la ragione"
              : !giusto ? "Scrivi il numero" : "Annulla il documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DDT() {
  const [rif, setRif] = useState(null);
  const [righe, setRighe] = useState([]);
  const [totale, setTotale] = useState(0);
  const [carico, setCarico] = useState(true);
  const [anno, setAnno] = useState("");
  const [autore, setAutore] = useState("");
  const [causale, setCausale] = useState("");
  const [cerca, setCerca] = useState("");
  const [aperto, setAperto] = useState(null);   // il DDT in modifica
  const [nuovo, setNuovo] = useState(false);
  const [importa, setImporta] = useState(false);
  const [daEliminare, setDaEliminare] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (anno) p.set("anno", anno);
      if (autore) p.set("autore", autore);
      if (causale) p.set("causale", causale);
      if (cerca.trim()) p.set("q", cerca.trim());
      const { data } = await api.get(`/ddt?${p.toString()}`);
      setRighe(data.ddt);
      setTotale(data.totale);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setCarico(false); }
  }, [anno, autore, causale, cerca]);

  const caricaRiferimenti = useCallback(() => {
    api.get("/ddt/riferimenti").then(({ data }) => setRif(data)).catch(() => {});
  }, []);

  useEffect(() => { caricaRiferimenti(); }, [caricaRiferimenti]);
  useEffect(() => {
    const t = setTimeout(carica, cerca ? 300 : 0);
    return () => clearTimeout(t);
  }, [carica, cerca]);

  const stampa = async (d, copie) => {
    try {
      // authFetch mette gia' il prefisso dell'API: rimetterlo qui
      // raddoppiava l'indirizzo e la stampa rispondeva 404.
      const r = await authFetch(`/ddt/${d.id}/pdf?copie=${copie}`);
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore nella stampa"); }
  };

  // Eliminare è definitivo e libera il numero: non basta un "sei sicuro?"
  // premuto per abitudine, si riscrive il numero del documento. È la stessa
  // fatica di leggerlo, ed è esattamente il controllo che serve.
  const elimina = async (d) => setDaEliminare(d);

  const eliminaDavvero = async (d, motivo) => {
    try {
      await api.delete(`/ddt/${d.id}`, { data: { motivo } });
      toast.success(`DDT ${d.numero} annullato`);
      setDaEliminare(null);
      carica(); caricaRiferimenti();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const chiudiModulo = (aggiornato) => {
    setAperto(null); setNuovo(false);
    if (aggiornato) { carica(); caricaRiferimenti(); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 pt-20 pb-24 space-y-4" data-testid="pagina-ddt">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-1">
            Documenti
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
            <Truck className="w-7 h-7 text-orange-500" /> Documenti di trasporto
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Il registro e il modulo sono la stessa cosa: il numero lo assegna
            l&apos;app, e si stampa in duplice copia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-12" data-testid="importa-ddt"
                  onClick={() => setImporta(true)}>
            <UploadCloud className="w-4 h-4 mr-2" /> Carica lo storico
          </Button>
          <Button onClick={() => setNuovo(true)} data-testid="nuovo-ddt"
                  className="h-12 bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuovo DDT
          </Button>
        </div>
      </div>

      <BarraFiltri
        cerca={cerca} onCerca={setCerca}
        segnaposto="Cerca numero, cliente, articolo, nota..."
        conteggio={totale} etichetta="DDT"
        filtri={[
          { valore: anno, onChange: setAnno, tutti: "Tutti gli anni",
            opzioni: rif?.anni || [], testid: "filtro-anno" },
          { valore: autore, onChange: setAutore, tutti: "Tutti gli autori",
            opzioni: rif?.autori || [], testid: "filtro-autore" },
          { valore: causale, onChange: setCausale, tutti: "Tutte le causali",
            opzioni: rif?.causali || [], testid: "filtro-causale" },
        ]} />

      {carico ? (
        <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>
      ) : righe.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center">
          <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessun documento di trasporto.</p>
        </CardContent></Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[52rem]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="p-3">Numero</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Autore</th>
                  <th className="p-3">Destinazione</th>
                  <th className="p-3">Descrizione</th>
                  <th className="p-3">Note</th>
                  <th className="p-3 text-right">Firme</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {righe.map((d) => {
                  const firmati = Object.keys(d.firme || {}).length;
                  const ann = d.annullato;
                  return (
                    <tr key={d.id} data-testid={`riga-ddt-${d.numero}`}
                        className={`border-b border-slate-50 last:border-0 ${
                          ann ? "bg-slate-50/80 text-slate-400 line-through decoration-red-400"
                              : "hover:bg-slate-50/60"}`}>
                      <td className="p-3 font-semibold tabular-nums whitespace-nowrap">
                        <button type="button" onClick={() => setAperto(d)}
                                className={ann ? "text-slate-500 hover:underline"
                                               : "text-[#0067b2] hover:underline"}>
                          {d.numero}
                        </button>
                      </td>
                      <td className="p-3 tabular-nums whitespace-nowrap">{giorno(d.data)}</td>
                      <td className="p-3">{d.autore}</td>
                      <td className="p-3">
                        {d.cliente}
                        {d.destinazione && d.destinazione !== "IDEM" && (
                          <span className="block text-[11px] text-slate-400">
                            → {d.destinazione}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {d.descrizione || d.causale}
                        {ann && (
                          <span className="block no-underline text-[11px] text-red-600 mt-0.5"
                                data-testid={`annullato-${d.numero}`}>
                            Annullato da {ann.da} il {giorno(ann.il)} · {ann.motivo}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-slate-500 max-w-[14rem] truncate" title={d.note}>
                        {d.note}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {firmati > 0 ? (
                          <span className="text-emerald-600 text-xs inline-flex items-center gap-1">
                            <PenLine className="w-3 h-3" /> {firmati}
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end no-underline">
                          <Button variant="outline" size="sm" onClick={() => setAperto(d)}
                                  data-testid={`modifica-${d.numero}`}
                                  title={ann ? "Guarda cosa conteneva" : "Modifica"}>
                            <Pen className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => stampa(d, 2)}
                                  data-testid={`stampa-${d.numero}`}
                                  title="Stampa in duplice copia">
                            <Printer className="w-3.5 h-3.5" />
                          </Button>
                          {!ann && (
                            <Button variant="outline" size="sm" onClick={() => elimina(d)}
                                    title="Annulla il documento"
                                    className="text-red-600 hover:bg-red-50">
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(nuovo || aperto) && (
        <ModuloDDT rif={rif} ddt={aperto}
                   onChiudi={() => chiudiModulo(false)}
                   onSalvato={() => chiudiModulo(true)} />
      )}

      {importa && (
        <ImportaStorico onChiudi={() => setImporta(false)}
                        onFatto={() => { carica(); caricaRiferimenti(); }} />
      )}

      {daEliminare && (
        <ConfermaAnnullamento ddt={daEliminare}
                              onChiudi={() => setDaEliminare(null)}
                              onConferma={(motivo) => eliminaDavvero(daEliminare, motivo)} />
      )}
    </div>
  );
}
